import { SkedAvatar as AvatarNew } from "@/components/ui/sked-avatar";
import React from "react";
import { useDispatch } from "react-redux";
import { Modal } from "@/components/modal/modal";
import { closeModal } from "libs/modals";
import { ADD_COLLABORATOR_MODAL } from ".";
import styled from "styled-components";
import { useRef, useState } from "react";
import { Icon } from "@dialogueconsulting/sked-ui";
import { toast } from "react-toastify";
import { getInstaUser } from "api/tags";
import { IgCollabPerson } from "../../../../../features/post/types";
import { Button } from "@/components/ui/button";

interface CollaboratorModalProps {
  beta?: boolean;
  title: string;
  notification: string;
  limit: number;
  collaboratorIg: IgCollabPerson[];
  accountId: string;

  onSubmit(tags: { [key: string]: any }[]): void;
}

/* Instagram handles are case-insensitive and never carry the @ that people type. */
const normaliseHandle = (value: string) =>
  value.trim().replace(/^@/, "").toLowerCase();

export const CollaboratorModal: React.FC<CollaboratorModalProps> = ({
  beta,
  title,
  notification,
  limit,
  onSubmit,
  collaboratorIg,
  accountId,
}) => {
  const dispatch = useDispatch();

  function closeCollaboratorModal() {
    dismissedRef.current = true;
    dispatch(closeModal({ type: ADD_COLLABORATOR_MODAL }));
  }

  const [isInputting, setIsInputting] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [unconfirmed, setUnconfirmed] = useState<string | null>(null);
  const [selectedCollaborator, setSelectedCollaborator] = useState(
    collaboratorIg && collaboratorIg.length > 0 ? collaboratorIg : []
  );
  /*
   * The field stays uncontrolled, so Continue has to read it from the DOM.
   * Continue used to submit `selectedCollaborator` alone, which meant a handle
   * typed and not committed with Enter was saved as no collaborator at all,
   * with nothing said.
   */
  const inputRef = useRef<HTMLInputElement>(null);
  // Cancel during an in-flight Continue lookup must not submit when it lands.
  const dismissedRef = useRef(false);

  /** Instagram's verdict on a handle, or null when it has none. */
  const lookUpCollaborator = async (
    username: string
  ): Promise<IgCollabPerson | null> => {
    try {
      setIsValidating(true);
      const validation = await checkInsta(accountId, username);
      return {
        username,
        profilePictureUrl: validation.profile_picture_url ?? "",
      };
    } catch {
      return null;
    } finally {
      setIsValidating(false);
    }
  };

  const handleEnterKey = async (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key !== "Enter" || !(event.target instanceof HTMLInputElement)) {
      return;
    }

    const input = event.target;
    const inputValue = normaliseHandle(input.value);
    if (inputValue === "") {
      return;
    }

    if (selectedCollaborator.find((user) => user.username === inputValue)) {
      toast.error("User Already Selected");
      return;
    }

    setUnconfirmed(null);
    const added = await lookUpCollaborator(inputValue);
    if (!added) {
      toast.error(
        "The user data was not found. The user either does not exist or is not a professional (Business/Creator) account."
      );
      return;
    }

    setSelectedCollaborator([...selectedCollaborator, added]);
    input.value = "";
  };

  const handleContinue = async () => {
    const typed = normaliseHandle(inputRef.current?.value ?? "");
    const alreadySelected = selectedCollaborator.some(
      (user) => user.username === typed
    );

    if (typed === "" || alreadySelected) {
      onSubmit(selectedCollaborator);
      closeCollaboratorModal();
      return;
    }

    setUnconfirmed(null);
    const added = await lookUpCollaborator(typed);
    if (dismissedRef.current) return;
    if (!added) {
      setUnconfirmed(typed);
      return;
    }

    if (inputRef.current) inputRef.current.value = "";
    onSubmit([...selectedCollaborator, added]);
    closeCollaboratorModal();
  };
  const handleCollaboratorRemoval = (key: string) => {
    setSelectedCollaborator(
      selectedCollaborator.filter((anItem) => anItem.username !== key)
    );
  };

  async function checkInsta(
    accountId: string,
    userInfo: string
  ): Promise<{ profile_picture_url?: string }> {
    const response = (await getInstaUser(accountId, userInfo)) as {
      data?: { profile_picture_url?: string };
      profile_picture_url?: string;
    };

    const data = "data" in response && response.data ? response.data : response;

    return { profile_picture_url: data.profile_picture_url };
  }

  return (
    <div>
      <Modal
        data-testid="add-user-modal"
        show
        onHide={closeCollaboratorModal}
        animation={false}
        // @ts-ignore
        bsStyle="giselle"
        // @ts-ignore
        bsSize="lg"
      >
        <Modal.Header closeButton onHide={closeCollaboratorModal}>
          <Modal.Title>
            <ModalTitle>
              <ModalTitleContent>{title}</ModalTitleContent>
              {beta && <ModalBeta>Beta</ModalBeta>}
            </ModalTitle>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Wrap>
            <ModalContent>
              <Notification>
                <Icon name="alert-circle" size="sm" color="#4F31FF"></Icon>
                <NotificationContent>{notification}</NotificationContent>
              </Notification>
              <CollaboratorWarp>
                <ModalInputWarp $limit={selectedCollaborator.length >= limit}>
                  <ModalInput
                    ref={inputRef}
                    type="text"
                    aria-invalid={Boolean(unconfirmed)}
                    aria-describedby={
                      unconfirmed ? "collaborator-unconfirmed" : undefined
                    }
                    placeholder={
                      selectedCollaborator.length >= limit
                        ? `Maximum ${limit} collaborators`
                        : isValidating
                          ? ""
                          : "Enter collaborator's username"
                    }
                    disabled={
                      selectedCollaborator.length >= limit || isValidating
                    }
                    onFocus={() => {
                      setIsInputting(true);
                    }}
                    onBlur={() => {
                      setIsInputting(false);
                    }}
                    onChange={() => setUnconfirmed(null)}
                    onKeyDown={handleEnterKey}
                  ></ModalInput>
                  {isInputting && selectedCollaborator.length < limit && (
                    <ModalInputTip>↵ enter to add</ModalInputTip>
                  )}
                </ModalInputWarp>
                <div
                  role="status"
                  aria-live="polite"
                  id="collaborator-unconfirmed"
                  className={
                    unconfirmed
                      ? "self-stretch text-sm leading-5 text-red-500"
                      : "sr-only"
                  }
                >
                  {unconfirmed && (
                    <span data-testid="collaborator-unconfirmed">
                      Instagram could not confirm @{unconfirmed}. Check the
                      spelling, or the account may not be a Business or Creator
                      account.
                    </span>
                  )}
                </div>
                <SelectedCollaborator
                  selectedCollaborator={selectedCollaborator}
                  handleCollaboratorRemoval={handleCollaboratorRemoval}
                ></SelectedCollaborator>
              </CollaboratorWarp>
            </ModalContent>
            <ModalFooterButtons>
              <Button
                variant="blue-secondary"
                size="md"
                onClick={closeCollaboratorModal}
              >
                Cancel
              </Button>
              <Button
                disabled={isValidating}
                variant="blue-primary"
                size="md"
                onClick={handleContinue}
              >
                Continue
              </Button>
            </ModalFooterButtons>
          </Wrap>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export const SelectedCollaborator: React.FC<{
  selectedCollaborator: IgCollabPerson[];
  handleCollaboratorRemoval?: (name: string) => void;
}> = ({ selectedCollaborator, handleCollaboratorRemoval }) => {
  return (
    <Collaborators>
      {selectedCollaborator.map((item) => (
        <Collaborator key={item.username}>
          <AvatarNew
            src={item.profilePictureUrl || undefined}
            alt={item.username || ""}
            platform="instagram"
            alert={false}
          ></AvatarNew>
          <CollaboratorContent>{item.username}</CollaboratorContent>
          <CollaboratorAction
            target="_blank"
            href={`https://www.instagram.com/${item.username}/`}
            onClick={(e) => {
              if (!item.username) {
                e.preventDefault();
                toast.error("Instagram account not found");
              }
            }}
          >
            <Icon name="link-external-01" size="sm" color="#4F31FF"></Icon>
          </CollaboratorAction>
          <CollaboratorAction
            onClick={() => {
              if (handleCollaboratorRemoval) {
                handleCollaboratorRemoval(item.username);
              }
            }}
          >
            <Icon name="x-close" size="sm" color="#808080"></Icon>
          </CollaboratorAction>
        </Collaborator>
      ))}
    </Collaborators>
  );
};

export const SelectedCollaboratorTag: React.FC<{
  selectedCollaborator: IgCollabPerson[];
}> = ({ selectedCollaborator }) => {
  return (
    <Collaborators>
      {selectedCollaborator.map((item: any) => (
        <CollaboratorTag key={item.username}>
          <CollaboratorContentTag>@{item.username}</CollaboratorContentTag>
          <CollaboratorAction
            target="_blank"
            href={`https://www.instagram.com/${item.username}/`}
            onClick={(e) => {
              if (!item) {
                e.preventDefault();
                toast.error("Instagram account not found");
              }
            }}
          >
            <Icon name="link-external-01" size="sm" color="#4F31FF"></Icon>
          </CollaboratorAction>
        </CollaboratorTag>
      ))}
    </Collaborators>
  );
};

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  flex-shrink: 0;
  background: #fff;
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  align-self: stretch;
`;
const ModalFooterButtons = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
`;
const ModalTitle = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
`;
const ModalTitleContent = styled.div`
  color: #0e1680;
  font-size: 24px;
  font-style: normal;
  line-height: 32px;
`;
const ModalBeta = styled.div`
  color: #808080;
  font-size: 12px;
  font-style: normal;
  line-height: 18px;
`;
const Notification = styled.div`
  display: flex;
  padding: 10px 14px;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  border-radius: 8px;
  background: #edeaff;
`;
const NotificationContent = styled.div`
  color: #4f31ff;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px;
`;

const CollaboratorWarp = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`;

const ModalInputWarp = styled.div<{ $limit: boolean }>`
  display: flex;
  justify-content: space-between;
  padding: 8px 14px;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  border-radius: 4px;
  border: 1px solid #c4c4c4;
  background: ${({ $limit }) => ($limit ? "#F2F2F2" : "#fff")};
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);

  &:focus-within {
    border: 1px solid #4f31ff;
  }
`;
const ModalInput = styled.input`
  width: 100%;
  border: none;
  font-size: 16px;
  font-style: normal;
  line-height: 24px;
  color: #333;

  background: none;

  ::placeholder,
  ::-webkit-input-placeholder {
    color: #808080;
  }

  &:focus {
    outline: none;
  }
`;
const ModalInputTip = styled.div`
  color: #808080;
  font-size: 10px;
  font-style: normal;
  line-height: 14px;
  flex-shrink: 0;
`;

const Collaborators = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 8px;
  flex-wrap: wrap;
`;
const Collaborator = styled.div`
  display: flex;
  padding: 4px 8px;
  align-items: center;
  gap: 8px;
  border-radius: 4px;
  background: #edeaff;
  text-align: center;
`;
const CollaboratorTag = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  border-radius: 4px;
  background: #ffff;
  text-align: center;
`;
const CollaboratorContent = styled.div`
  display: flex;
  color: #333;
  align-items: center;

  &:hover {
    cursor: default;
  }
`;
const CollaboratorContentTag = styled.div`
  display: flex;
  align-items: center;

  &:hover {
    cursor: default;
  }

  color: #000;
`;
const CollaboratorAction = styled.a`
  display: flex;
  align-items: center;

  &:hover {
    cursor: pointer;
  }
`;
